// Client-side mirror of the engine result shapes (server/services/aca/types.ts)
// used by the Dashboard and Employees tabs. Views only — nothing here computes
// compliance.

export type AcaStatus = "full_time" | "non_full_time" | "measuring" | "not_employed" | "unknown";
export type Affordability = "affordable" | "not_affordable" | "pending_data" | "not_applicable";
export type ExceptionSeverity = "blocking" | "warning" | "info";

export interface AcaException {
  code: string;
  severity: ExceptionSeverity;
  message: string;
  employeeId?: number;
  month?: number;
  fix?: { area: string; hint?: string };
}

export interface MonthResult {
  month: number;
  employed: boolean;
  employedFullMonth: boolean;
  terminatedMidMonth: boolean;
  acaStatus: AcaStatus;
  hours: number | null;
  hoursSource: "monthly_hours" | "schedule_estimate" | "measurement_average" | "none" | null;
  lnapReason: string | null;
  offered: boolean | null;
  offerPlanId: number | null;
  offerPlanName: string | null;
  offerMinimumValue: boolean | null;
  offerSpouse: boolean | null;
  offerDependents: boolean | null;
  line14: string | null;
  line15: number | null;
  line15Source: "derived" | "manual_override" | null;
  line16: string | null;
  enrolled: boolean;
  enrolledPlanId: number | null;
  safeHarbor: "fpl" | "rate_of_pay" | "w2" | null;
  threshold: number | null;
  affordabilityPct: number | null;
  planYearStart: string | null;
  affordability: Affordability;
  exceptions: AcaException[];
  explanation?: string[];
  inputs?: Record<string, unknown>;
}

export interface EmployeeYearResult {
  employeeId: number;
  firstName: string;
  lastName: string;
  employmentType: string | null;
  employeeClassId: number | null;
  compensationType: string | null;
  hireDate: string | null;
  terminationDate: string | null;
  periods: Array<{ start: string; end: string | null }>;
  formRequired: "full_time" | "self_insured_enrolled" | null;
  annualCode1G: boolean;
  planStartMonth: string;
  months: MonthResult[];
  summary: {
    employedMonths: number;
    fullTimeMonths: number;
    lnapMonths: number;
    offeredMonths: number;
    enrolledMonths: number;
    affordableMonths: number;
    notAffordableMonths: number;
    pendingMonths: number;
    status: Affordability | "mixed";
  };
  coveredIndividuals: any[];
  exceptions: AcaException[];
}

export interface AleResult {
  taxYear: number;
  basisYear: number;
  monthlyFullTime: number[];
  monthlyFte: number[];
  monthlyTotal: number[];
  averageFte: number;
  isAle: boolean;
  source: string;
  estimated: boolean;
  monthsWithData: number;
  overrideReason?: string | null;
  aggregatedGroup: boolean;
  exceptions: AcaException[];
}

export interface SnapshotMeta {
  id: number;
  version: number;
  status: string;
  computedAt: string | null;
  computedByUserId?: string | null;
  engineVersion?: string;
}

export interface AcaYearResult {
  clientId: number;
  taxYear: number;
  computedAt: string;
  engineVersion: string;
  ale: AleResult;
  form1094: { monthlyFtCount: number[]; monthlyTotalCount: number[]; monthlyMecOffered: boolean[]; employeeCountDay: string; totalForms1095c: number; isAggregatedGroup: boolean };
  selfInsured: boolean;
  method: "monthly" | "look-back";
  employees: EmployeeYearResult[];
  exceptions: AcaException[];
  stats: { population: number; formsRequired: number; blocking: number; warnings: number };
}

export interface DashboardExceptionRow {
  code: string;
  severity: ExceptionSeverity;
  count: number;
  employees: number;
  message: string;
  fix?: { area: string; hint?: string };
}

export interface DashboardData {
  taxYear: number;
  snapshot: SnapshotMeta;
  status: string;
  isLocked: boolean;
  ale: AleResult;
  form1094: AcaYearResult["form1094"];
  selfInsured: boolean;
  method: "monthly" | "look-back";
  population: number;
  fullTimeEmployees: number;
  formsRequired: number;
  affordability: { affordable: number; notAffordable: number; pending: number };
  exceptions: DashboardExceptionRow[];
  stats: AcaYearResult["stats"];
  deadlines: { taxYear: number; furnishToEmployees: string; paperFiling: string; electronicFiling: string };
}

export interface MonthlyHoursRow { employeeId: number; month: number; hours: string | number; source: string }
export interface W2Row { employeeId: number; box1Wages: string | number | null; isFinal: boolean }

export const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const ACA_STATUS_LABELS: Record<AcaStatus, string> = {
  full_time: "ACA full-time",
  non_full_time: "Not full-time",
  measuring: "Measuring",
  not_employed: "Not employed",
  unknown: "Unknown",
};

export const LNAP_LABELS: Record<string, string> = {
  first_partial_month: "First partial month of employment",
  waiting_period: "Waiting period (new full-time hire)",
  initial_measurement_period: "Initial measurement period",
  first_ale_year: "First year as ALE (Jan–Mar)",
};

export const AFFORDABILITY_LABELS: Record<string, string> = {
  affordable: "Affordable",
  not_affordable: "Not affordable",
  pending_data: "Pending data",
  not_applicable: "Not applicable",
  mixed: "Mixed",
};

export const SAFE_HARBOR_LABELS: Record<string, string> = {
  fpl: "Federal poverty line",
  rate_of_pay: "Rate of pay",
  w2: "W-2 wages",
};

export const FIX_AREA_LABELS: Record<string, string> = {
  employees: "Employees tab",
  policies: "Policies tab",
  affordability: "Affordability tab",
  settings: "Settings tab",
  forms: "Forms tab",
  hr: "Employee record (HR)",
  benefits: "Benefits / plans",
  plan_library: "Plan library",
};

export const REPORTING_STATUS_LABELS: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  needs_attention: "Needs attention",
  ready: "Ready to generate",
  finalized: "Finalized",
};

export const REPORTING_STATUS_COLORS: Record<string, string> = {
  not_started: "bg-gray-500",
  in_progress: "bg-blue-500",
  needs_attention: "bg-amber-500",
  ready: "bg-green-600",
  finalized: "bg-emerald-700",
};

export const money = (v: number | null | undefined) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
};
export const taxYearOptions = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => current - i);
};
